/**
 * Shown to org-only accounts that have no active organization: they cannot use
 * the personal context, so they must accept an invitation (or enter an org they
 * already belong to) before reaching the app. Only rendered when personal scope
 * is disabled AND there is no active org (see AuthedLayout).
 *
 * The active org is session-bound, so a fresh login always starts with none —
 * which would re-prompt a single-org member on every sign-in. To avoid that, a
 * member of exactly one org is entered automatically; the join UI only appears
 * when there is a real choice (0 orgs → await/accept, ≥2 → pick).
 */
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Building2, LogOut, Mailbox, Plus } from 'lucide-react'
import { useAuth } from '../lib/auth'
import { useOrganizations } from '../hooks/useOrganizations'
import { useMyInvitations } from '../hooks/useMyInvitations'
import { logout } from '../lib/endpoints/auth'
import { qk } from '../lib/queryKeys'
import { Button } from './ui/button'
import { BrandHeader } from './Logo'
import { TESTID } from '../lib/testids'
import { CreateOrganizationDialog } from './CreateOrganizationDialog'
import { ploteaApp } from '../lib/productProfile'

export function OnboardingScreen() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const auth = useAuth()
  const { query: orgsQuery, setActive } = useOrganizations()
  const { query: invitesQuery, accept, reject } = useMyInvitations()

  const orgs = orgsQuery.data ?? []
  const invites = invitesQuery.data ?? []
  const busy = setActive.isPending || accept.isPending || reject.isPending
  const canCreate =
    auth.status === 'authenticated' && auth.user.can_create_organization
  // Plotea: anyone with a verified address may create an organization, so an
  // unverified account is told how to unlock it instead of "ask an admin".
  const mustVerifyEmail =
    ploteaApp &&
    !canCreate &&
    auth.status === 'authenticated' &&
    !auth.user.email_verified
  const [showCreate, setShowCreate] = useState(false)

  // Exactly one org → enter it automatically (fired once; on error we fall back
  // to the manual UI so the user isn't stuck in a retry loop).
  const soleOrg = orgsQuery.isSuccess && orgs.length === 1 ? orgs[0] : null
  const autoEntered = useRef(false)
  useEffect(() => {
    if (autoEntered.current || !soleOrg || setActive.isPending) return
    autoEntered.current = true
    setActive.mutate(soleOrg.id, {
      onError: () => {
        autoEntered.current = false
      },
    })
  }, [soleOrg, setActive])

  const roleLabel = (r: string) =>
    t(`organizations.role.${r}`, { defaultValue: r })

  const enterOrg = (id: string) =>
    setActive.mutate(id, {
      onError: () => toast.error(t('organizations.toast.error')),
    })

  const acceptInvite = (invitationId: string, organizationId: string) =>
    accept.mutate(
      { invitationId, organizationId },
      {
        onSuccess: () => toast.success(t('organizations.onboarding.accepted')),
        onError: () => toast.error(t('organizations.toast.error')),
      },
    )

  const rejectInvite = (invitationId: string) =>
    reject.mutate(invitationId, {
      onSuccess: () => toast.success(t('organizations.invitations.rejected')),
      onError: () => toast.error(t('organizations.toast.error')),
    })

  const doLogout = () =>
    void logout().finally(() => {
      qc.removeQueries({ queryKey: qk.me() })
      window.location.assign('/login')
    })

  // Resolving orgs, or auto-entering the sole org → transient state, not the
  // join screen (unless auto-enter failed, in which case fall through).
  if (orgsQuery.isLoading || (soleOrg && !setActive.isError)) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background p-4"
        data-testid={TESTID.onboardingScreen}
      >
        <p className="text-sm text-muted-foreground">
          {t('organizations.onboarding.entering')}
        </p>
      </div>
    )
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background p-4"
      data-testid={TESTID.onboardingScreen}
    >
      <div className="w-full max-w-md rounded-[3px] border border-border bg-card p-6">
        <div className="mb-5">
          <BrandHeader topic="Organization setup / 01" compact />
        </div>

        <h2 className="text-base font-semibold text-foreground">
          {t('organizations.onboarding.title')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('organizations.onboarding.subtitle')}
        </p>

        {orgs.length > 0 && (
          <div className="mt-5">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('organizations.onboarding.yourOrgs')}
            </h3>
            <div className="space-y-2" data-testid={TESTID.onboardingOrgs}>
              {orgs.map((org) => (
                <Button
                  key={org.id}
                  variant="outline"
                  className="w-full justify-start gap-2"
                  disabled={busy}
                  data-testid={TESTID.onboardingEnterOrg(org.id)}
                  onClick={() => enterOrg(org.id)}
                >
                  <Building2 className="size-4 shrink-0" />
                  <span className="truncate">{org.name}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {invites.length > 0 && (
          <div className="mt-5">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('organizations.onboarding.invitations')}
            </h3>
            <div className="space-y-2" data-testid={TESTID.onboardingInvites}>
              {invites.map((inv) => (
                <div
                  key={inv.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-border p-3"
                  data-testid={TESTID.onboardingInviteRow}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {inv.organizationName ?? inv.organizationId}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {t('organizations.invitations.meta', {
                        role: roleLabel(inv.role),
                        inviter:
                          inv.inviterName ??
                          inv.inviterEmail ??
                          t('organizations.invitations.unknownInviter'),
                      })}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      data-testid={TESTID.onboardingRejectInvite(inv.id)}
                      onClick={() => rejectInvite(inv.id)}
                    >
                      {t('organizations.invitations.reject')}
                    </Button>
                    <Button
                      size="sm"
                      disabled={busy}
                      data-testid={TESTID.onboardingAcceptInvite(inv.id)}
                      onClick={() => acceptInvite(inv.id, inv.organizationId)}
                    >
                      {t('organizations.onboarding.accept')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {orgs.length === 0 && invites.length === 0 && (
          <div className="mt-5 space-y-3 rounded-lg border border-dashed border-border p-4">
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <Mailbox className="mt-0.5 size-4 shrink-0" />
              <span
                data-testid={
                  mustVerifyEmail ? TESTID.onboardingVerifyEmail : undefined
                }
              >
                {t(
                  canCreate
                    ? 'organizations.onboarding.awaitingCreator'
                    : mustVerifyEmail
                      ? 'organizations.onboarding.verifyEmailToCreate'
                      : 'organizations.onboarding.awaiting',
                )}
              </span>
            </div>
            {canCreate && (
              <Button
                className="w-full gap-2"
                data-testid={TESTID.onboardingCreateOrg}
                onClick={() => setShowCreate(true)}
              >
                <Plus className="size-4" />
                {t('organizations.create.button')}
              </Button>
            )}
          </div>
        )}

        <div className="mt-6 border-t border-border pt-4">
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
            data-testid={TESTID.onboardingLogout}
            onClick={doLogout}
          >
            <LogOut className="size-4" />
            {t('organizations.onboarding.logout')}
          </Button>
        </div>
      </div>
      {canCreate && (
        <CreateOrganizationDialog
          open={showCreate}
          onOpenChange={setShowCreate}
        />
      )}
    </div>
  )
}
